import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { apiFetch, buildQuery } from '../utils/api';

const UNITES = ['Stagiaire', 'Groupe', 'Heure', 'Forfait', 'Jour'];

function champ(label, children, required = false) {
  return (
    <div style={{ marginBottom: '0.9rem' }}>
      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
        {label}{required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '0.5rem 0.7rem', borderRadius: '6px',
  border: '1px solid var(--border)', background: 'var(--bg-elevated)',
  color: 'var(--text-primary)', fontSize: '0.9rem', boxSizing: 'border-box',
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

export default function DevisForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedSessionId = searchParams.get('session');

  const { data: clientsData }    = useApi('/clients',    { limit: 500 }, []);
  const { data: formationsData } = useApi('/formations', { limit: 500 }, []);
  const { data: sessionPreData } = useApi(
    preselectedSessionId ? `/sessions/${preselectedSessionId}` : null, {}, [preselectedSessionId]
  );
  const { data: parametresData } = useApi('/parametres', {}, []);

  const [form, setForm] = useState({
    client_id: '', client_name: '', email_client: '',
    code_formation: '', intitule_formation: '',
    unite: 'Stagiaire', qte: 1, pu_ht: '', remise_eur: 0, taux_remise_pct: 0,
    duree_heures: '', prerequis: '', notes_internes: '', note_devis: '',
    date_demande: new Date().toISOString().split('T')[0],
    date_validite: '',
    session_id: preselectedSessionId || '',
  });

  // Ref : true si l'utilisateur a saisi manuellement la date de validité
  // → empêche l'écrasement lors d'un changement de date_demande, mais pas lors du chargement des paramètres
  const dateValiditeManuelle = useRef(false);

  const [sessionsSelectionnees, setSessionsSelectionnees] = useState([]);
  const [formationSessions,     setFormationSessions]     = useState([]); // sessions filtrées par formation
  const [sessionsOpen,          setSessionsOpen]          = useState(false);
  const [nextId,              setNextId]              = useState('');
  const [saving,              setSaving]              = useState(false);
  const [error,               setError]               = useState('');
  const [preview,             setPreview]             = useState(false);
  const [previewHtml,         setPreviewHtml]         = useState('');
  const [previewLoading,      setPreviewLoading]      = useState(false);
  const [formationInputVal,   setFormationInputVal]   = useState('');
  const [showFormationDrop,   setShowFormationDrop]   = useState(false);

  const baseHt     = (parseFloat(form.pu_ht) || 0) * (parseFloat(form.qte) || 1);
  const remisePct  = parseFloat(form.taux_remise_pct) || 0;
  const remiseEur  = remisePct > 0 ? Math.round(baseHt * remisePct) / 100 : (parseFloat(form.remise_eur) || 0);
  const totalHt    = baseHt - remiseEur;
  const tva      = totalHt * 0.2;
  const totalTtc = totalHt + tva;

  useEffect(() => {
    apiFetch('/devis-next-id').then(d => setNextId(d.next_id)).catch(() => {});
  }, []);

  // Pré-remplir depuis la session passée en URL
  useEffect(() => {
    if (sessionPreData && preselectedSessionId) {
      setSessionsSelectionnees([sessionPreData]);
      setForm(f => ({
        ...f,
        session_id:         sessionPreData.session_id || '',
        intitule_formation: sessionPreData.intitule_formation || f.intitule_formation,
        code_formation:     sessionPreData.formation_id || f.code_formation,
        duree_heures:       sessionPreData.duree || f.duree_heures,
      }));
    }
  }, [sessionPreData, preselectedSessionId]);

  // Synchroniser l'affichage du champ formation quand code_formation est pré-rempli (duplication)
  useEffect(() => {
    if (!form.code_formation || !formationsData?.data) return;
    const fo = formationsData.data.find(f => f.formation_id === parseInt(form.code_formation));
    if (fo) setFormationInputVal(`[${fo.numero || fo.formation_id}] ${fo.intitule}`);
  }, [form.code_formation, formationsData]);

  // Pré-charger les sessions dès qu'une formation est sélectionnée
  useEffect(() => {
    if (!form.code_formation) { setFormationSessions([]); return; }
    apiFetch(`/sessions${buildQuery({ formation_id: form.code_formation, limit: 100, date_from: new Date().toISOString().split('T')[0] })}`)
      .then(d => setFormationSessions(d.data || []))
      .catch(() => setFormationSessions([]));
  }, [form.code_formation]);


  function toggleSession(s) {
    const already = sessionsSelectionnees.some(x => x.session_id === s.session_id);
    const updated = already
      ? sessionsSelectionnees.filter(x => x.session_id !== s.session_id)
      : [...sessionsSelectionnees, s];
    setSessionsSelectionnees(updated);
    setForm(f => ({ ...f, session_id: updated[0]?.session_id || '' }));
  }

  function handleClientChange(clientId) {
    const client = clientsData?.data?.find(c => c.client_id === clientId);
    setForm(f => ({
      ...f,
      client_id:    clientId,
      client_name:  client?.nom_client    || f.client_name,
      email_client: client?.contact_email || f.email_client,
    }));
    if (clientId) {
      apiFetch(`/clients/${clientId}/taux-remise`).then(d => {
        if (d.taux_remise > 0) setForm(f => ({ ...f, taux_remise_pct: d.taux_remise }));
      }).catch(() => {});
    }
  }

  function handleFormationChange(formationId) {
    // Si la formation change, réinitialiser les sessions liées (elles ne correspondent plus)
    if (formationId !== form.code_formation) {
      setSessionsSelectionnees([]);
      setForm(f => ({ ...f, session_id: '' }));
    }
    const formation = formationsData?.data?.find(f => f.formation_id === parseInt(formationId));
    if (!formation) { setForm(f => ({ ...f, code_formation: formationId })); return; }
    const sta    = formation.cout_stagiaire_ht;
    const grp    = formation.cout_groupe_ht;
    const hasSta = sta !== null && sta !== undefined && sta !== '' && parseFloat(sta) > 0;
    const hasGrp = grp !== null && grp !== undefined && grp !== '' && parseFloat(grp) > 0;
    const prixAuto  = hasSta ? parseFloat(sta) : hasGrp ? parseFloat(grp) : '';
    const uniteAuto = hasSta ? 'Stagiaire' : hasGrp ? 'Groupe' : 'Stagiaire';
    setForm(f => ({
      ...f,
      code_formation:     formationId,
      intitule_formation: formation.intitule   || f.intitule_formation,
      duree_heures:       formation.duree_heures || f.duree_heures,
      prerequis:          formation.prerequis   || f.prerequis,
      pu_ht:              prixAuto !== '' ? prixAuto : f.pu_ht,
      unite:              uniteAuto,
    }));
  }

  // Calcul date_validite — deux déclencheurs séparés pour corriger le bug de timing :
  // L'ancien code vérifiait !form.date_validite, ce qui bloquait le recalcul quand les
  // paramètres arrivaient APRÈS que le fallback (30 j) avait déjà rempli le champ.

  // 1) Quand les paramètres chargent → toujours recalculer (paramètre réel prioritaire sur le fallback)
  //    sauf si l'utilisateur a saisi la date manuellement
  useEffect(() => {
    if (!parametresData || !form.date_demande) return;
    if (dateValiditeManuelle.current) return;
    const jours = parseInt(parametresData.devis_validite_jours) || 30;
    const d = new Date(form.date_demande);
    d.setDate(d.getDate() + jours);
    setForm(f => ({ ...f, date_validite: d.toISOString().split('T')[0] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parametresData]);

  // 2) Quand date_demande change → recalculer si l'utilisateur n'a pas saisi manuellement
  useEffect(() => {
    if (!form.date_demande || dateValiditeManuelle.current) return;
    const jours = parseInt(parametresData?.devis_validite_jours) || 30;
    const d = new Date(form.date_demande);
    d.setDate(d.getDate() + jours);
    setForm(f => ({ ...f, date_validite: d.toISOString().split('T')[0] }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.date_demande]);

  function buildBody() {
    return {
      ...form,
      net_ht:           totalHt,
      total_ht:         totalHt,
      qte:              parseFloat(form.qte)          || 1,
      pu_ht:            parseFloat(form.pu_ht)         || 0,
      remise:           remiseEur,
      taux_remise_pct:  remisePct,
      duree_heures:     parseFloat(form.duree_heures)  || null,
      code_formation:   parseInt(form.code_formation)  || null,
      session_ids:           sessionsSelectionnees.map(s => s.session_id),
      sessions_possibles_data: sessionsSelectionnees,
    };
  }

  async function handlePreview() {
    setPreviewLoading(true); setError('');
    try {
      const API_BASE    = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
      const storedToken = localStorage.getItem('gf_token');
      const res = await fetch(`${API_BASE}/devis-preview-temp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${storedToken}` },
        body: JSON.stringify({ ...buildBody(), devis_id: nextId || '' }),
      });
      if (res.ok) { setPreviewHtml(await res.text()); setPreview(true); }
      else { const e = await res.json().catch(() => ({})); setError(e.error || `Erreur ${res.status}`); }
    } catch (e) { setError(e.message); }
    finally { setPreviewLoading(false); }
  }

  async function handleSave() {
    if (!form.client_id)          return setError('Veuillez sélectionner un client dans la liste');
    if (!form.intitule_formation) return setError('La formation est requise');
    if (!form.pu_ht)              return setError('Le prix unitaire est requis');
    setSaving(true); setError('');
    try {
      const result = await apiFetch('/devis', { method: 'POST', body: JSON.stringify(buildBody()) });
      if (result?.devis_id) navigate(`/devis/${result.devis_id}`);
      else setError('Devis créé mais ID manquant : ' + JSON.stringify(result));
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  function formatLieu(lieu) {
    if (!lieu) return '—';
    const first = lieu.split(/\s*[–\-]\s*/)[0].trim();
    return first.toLowerCase().startsWith('cej') ? 'CEJ' : (first || '—');
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 className="page-title">Nouveau devis</h1>
          <p className="page-subtitle">
            {nextId
              ? <span>N° attribué : <strong style={{ color: 'var(--accent-blue)', fontSize: '1rem' }}>{nextId}</strong></span>
              : <span style={{ color: 'var(--text-tertiary)' }}>Calcul du numéro...</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={() => navigate(-1)}>← Annuler</button>
          <button className="btn-secondary" onClick={handlePreview} disabled={previewLoading}>
            {previewLoading ? '⏳' : '👁 Prévisualiser'}
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : '✓ Créer le devis'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '0.7rem 1rem', marginBottom: '1rem', color: '#f87171' }}>
          {error}
        </div>
      )}

      <div className="grid-2" style={{ gap: '1rem' }}>

        {/* ── Colonne gauche ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Client */}
          <div className="card">
            <h3 className="card-title">Client</h3>
            {champ('Sélectionner un client *', (
              <select value={form.client_id} onChange={e => handleClientChange(e.target.value)} style={inputStyle}>
                <option value="">— Choisir un client —</option>
                {clientsData?.data?.map(c => <option key={c.client_id} value={c.client_id}>{c.nom_client}</option>)}
              </select>
            ))}
            {champ('Email client', (
              <input type="email" value={form.email_client} onChange={e => set('email_client', e.target.value)}
                placeholder="Pour envoi du lien de validation" style={inputStyle} />
            ))}
          </div>

          {/* Formation */}
          <div className="card">
            <h3 className="card-title">Formation</h3>
            {champ('Sélectionner une formation du catalogue', (
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={formationInputVal}
                  onChange={e => { setFormationInputVal(e.target.value); setShowFormationDrop(true); }}
                  onFocus={() => setShowFormationDrop(true)}
                  onBlur={() => setTimeout(() => setShowFormationDrop(false), 150)}
                  placeholder="Rechercher par N° ou nom de formation…"
                  style={inputStyle}
                />
                {showFormationDrop && (
                  <div style={{ position: 'absolute', zIndex: 200, width: '100%', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px', boxShadow: '0 4px 16px rgba(0,0,0,0.18)', maxHeight: '260px', overflowY: 'auto', marginTop: '2px' }}>
                    {formationInputVal && (
                      <div onMouseDown={() => { setForm(f => ({ ...f, code_formation: '', intitule_formation: '' })); setFormationInputVal(''); setShowFormationDrop(false); }}
                        style={{ padding: '0.5rem 0.9rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '0.82rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                        ✕ Effacer la sélection
                      </div>
                    )}
                    {(formationsData?.data || [])
                      .filter(f => {
                        const term = formationInputVal.toLowerCase();
                        return !term || String(f.numero || f.formation_id).toLowerCase().includes(term) || f.intitule?.toLowerCase().includes(term);
                      })
                      .map(f => (
                        <div key={f.formation_id}
                          onMouseDown={() => { handleFormationChange(f.formation_id); setFormationInputVal(`[${f.numero || f.formation_id}] ${f.intitule}`); setShowFormationDrop(false); }}
                          style={{ padding: '0.6rem 0.9rem', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                          onMouseLeave={e => e.currentTarget.style.background = ''}>
                          <strong style={{ color: 'var(--accent-blue)' }}>[{f.numero || f.formation_id}]</strong> {f.intitule}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
            {champ('Intitulé de la formation', (
              <input type="text" value={form.intitule_formation} onChange={e => set('intitule_formation', e.target.value)}
                placeholder="Intitulé exact sur le devis" style={inputStyle} />
            ), true)}
            {champ('Durée (heures)', (
              <input type="number" value={form.duree_heures} onChange={e => set('duree_heures', e.target.value)}
                placeholder="Ex: 7" style={inputStyle} />
            ))}
            {champ('Prérequis / Aptitude', (
              <input type="text" value={form.prerequis} onChange={e => set('prerequis', e.target.value)}
                placeholder="-" style={inputStyle} />
            ))}
          </div>

        </div>

        {/* ── Colonne droite ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Tarif */}
          <div className="card">
            <h3 className="card-title">Tarif</h3>
            {form.code_formation && (() => {
              const f   = formationsData?.data?.find(f => f.formation_id === parseInt(form.code_formation));
              const sta = f ? parseFloat(f.cout_stagiaire_ht) : 0;
              const grp = f ? parseFloat(f.cout_groupe_ht) : 0;
              if (!f || (!sta && !grp)) return null;
              return (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.8rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>Prix catalogue :</span>
                  {sta > 0 && (
                    <button type="button" onClick={() => setForm(f => ({ ...f, pu_ht: sta, unite: 'Stagiaire' }))}
                      style={{ padding: '0.25rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: form.unite === 'Stagiaire' && parseFloat(form.pu_ht) === sta ? 'var(--accent-blue)' : 'var(--bg-elevated)', color: form.unite === 'Stagiaire' && parseFloat(form.pu_ht) === sta ? '#fff' : 'var(--text-primary)', fontSize: '0.82rem', cursor: 'pointer' }}>
                      👤 Stagiaire : {sta.toFixed(2)} €
                    </button>
                  )}
                  {grp > 0 && (
                    <button type="button" onClick={() => setForm(f => ({ ...f, pu_ht: grp, unite: 'Groupe' }))}
                      style={{ padding: '0.25rem 0.7rem', borderRadius: '6px', border: '1px solid var(--border)', background: form.unite === 'Groupe' && parseFloat(form.pu_ht) === grp ? 'var(--accent-blue)' : 'var(--bg-elevated)', color: form.unite === 'Groupe' && parseFloat(form.pu_ht) === grp ? '#fff' : 'var(--text-primary)', fontSize: '0.82rem', cursor: 'pointer' }}>
                      👥 Groupe : {grp.toFixed(2)} €
                    </button>
                  )}
                </div>
              );
            })()}
            <div className="form-grid-3" style={{ gap: '0.5rem' }}>
              <div>{champ('Unité', (
                <select value={form.unite} onChange={e => set('unite', e.target.value)} style={inputStyle}>
                  {UNITES.map(u => <option key={u}>{u}</option>)}
                </select>
              ))}</div>
              <div>{champ('Prix unitaire HT (€)', (
                <input type="number" value={form.pu_ht} onChange={e => set('pu_ht', e.target.value)}
                  placeholder="160.00" step="0.01" style={inputStyle} />
              ), true)}</div>
              <div>{champ('Quantité', (
                <input type="number" value={form.qte} onChange={e => set('qte', e.target.value)} min="1" style={inputStyle} />
              ))}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.7rem' }}>
              {champ('Remise (%)', (
                <input type="number" value={form.taux_remise_pct} onChange={e => set('taux_remise_pct', e.target.value)} min="0" max="100" step="0.5" style={inputStyle} />
              ))}
              {champ('Montant remise (€)', (
                <input type="number" value={remiseEur.toFixed(2)} readOnly style={{ ...inputStyle, background: 'var(--bg-elevated)', color: 'var(--text-secondary)' }} />
              ))}
            </div>
            <div style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '0.8rem 1rem', marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', marginBottom: '0.3rem' }}>
                <span>Total HT</span><strong>{totalHt.toFixed(2)} €</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.3rem' }}>
                <span>TVA 20%</span><span>{tva.toFixed(2)} €</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 700, borderTop: '1px solid var(--border)', paddingTop: '0.4rem', marginTop: '0.3rem' }}>
                <span>Total TTC</span><span style={{ color: 'var(--accent-blue)' }}>{totalTtc.toFixed(2)} €</span>
              </div>
            </div>
          </div>

          {/* Session(s) disponible(s) */}
          <div className="card">
            {/* En-tête cliquable */}
            <div
              onClick={() => form.code_formation && setSessionsOpen(o => !o)}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: form.code_formation ? 'pointer' : 'default', userSelect: 'none' }}
            >
              <h3 className="card-title" style={{ margin: 0 }}>
                Session(s) disponible(s)
                <span style={{ fontSize: '0.8rem', fontWeight: 400, color: 'var(--text-tertiary)', marginLeft: '0.5rem' }}>(dates proposées au client)</span>
                {sessionsSelectionnees.length > 0 && (
                  <span style={{ marginLeft: '0.6rem', background: 'var(--accent-blue)', color: '#fff', borderRadius: '10px', padding: '0.05rem 0.5rem', fontSize: '0.72rem', fontWeight: 700 }}>
                    {sessionsSelectionnees.length} sélectionnée{sessionsSelectionnees.length > 1 ? 's' : ''}
                  </span>
                )}
              </h3>
              {form.code_formation && (
                <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem 0.4rem', color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1, transform: sessionsOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s ease' }}>
                  ▼
                </button>
              )}
            </div>

            {!form.code_formation ? (
              <p style={{ fontSize: '0.84rem', color: 'var(--text-tertiary)', fontStyle: 'italic', marginTop: '0.5rem' }}>
                Sélectionnez d'abord une formation pour afficher les sessions.
              </p>
            ) : !sessionsOpen ? null
            : formationSessions.length === 0 ? (
              <p style={{ fontSize: '0.84rem', color: 'var(--text-tertiary)', fontStyle: 'italic', marginTop: '0.5rem' }}>
                Aucune session trouvée pour cette formation.
              </p>
            ) : (() => {
              const formation = formationsData?.data?.find(f => f.formation_id === parseInt(form.code_formation));
              const maxInscrits = formation?.stagiaires_max;
              return (
                <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden', marginTop: '0.7rem' }}>
                  {/* En-têtes */}
                  <div style={{ display: 'grid', gridTemplateColumns: '36px 120px 1fr 80px 70px', background: '#eef0f2', borderBottom: '1px solid #d1d5db' }}>
                    <div />
                    {['N° session', 'Dates', 'Lieu', 'Inscrits'].map((h, i) => (
                      <div key={i} style={{ padding: '0.35rem 0.6rem', fontSize: '0.72rem', fontWeight: 600, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</div>
                    ))}
                  </div>
                  {/* Lignes */}
                  {formationSessions.map((s, i) => {
                    const checked = sessionsSelectionnees.some(x => x.session_id === s.session_id);
                    return (
                      <div key={s.session_id} onClick={() => toggleSession(s)}
                        style={{ display: 'grid', gridTemplateColumns: '36px 120px 1fr 80px 70px', background: checked ? 'rgba(59,130,246,0.07)' : i % 2 === 0 ? '#fff' : '#f9fafb', borderBottom: i < formationSessions.length - 1 ? '1px solid #e5e7eb' : 'none', alignItems: 'center', cursor: 'pointer' }}>
                        <div style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleSession(s)}
                            onClick={e => e.stopPropagation()} style={{ cursor: 'pointer', accentColor: 'var(--accent-blue)' }} />
                        </div>
                        <div style={{ padding: '0.4rem 0.6rem', fontWeight: 700, fontSize: '0.83rem', color: 'var(--accent-blue)' }}>{s.session_id}</div>
                        <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.81rem', color: '#374151' }}>
                          {s.date_debut ? `${formatDate(s.date_debut)} → ${formatDate(s.date_fin)}` : '—'}
                        </div>
                        <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.81rem', color: '#374151' }}>{formatLieu(s.lieu)}</div>
                        <div style={{ padding: '0.4rem 0.6rem', fontSize: '0.81rem', fontWeight: 600, color: '#E87722' }}>
                          {s.nb_inscrits || 0}{maxInscrits ? `/${maxInscrits}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            <p style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', marginTop: '0.5rem' }}>
              Le client choisira parmi ces dates lors de la validation du devis. La session deviendra liée au devis une fois les stagiaires inscrits.
            </p>
          </div>

          {/* Dates du devis */}
          <div className="card">
            <h3 className="card-title">Dates du devis</h3>
            <div className="grid-2" style={{ gap: '0.5rem' }}>
              <div>{champ('Date de la demande', (
                <input type="date" value={form.date_demande} onChange={e => set('date_demande', e.target.value)} style={inputStyle} />
              ))}</div>
              <div>{champ('Validité du devis', (
                <input type="date" value={form.date_validite}
                  onChange={e => { dateValiditeManuelle.current = true; set('date_validite', e.target.value); }}
                  style={inputStyle} />
              ))}</div>
            </div>
            {champ('Note (visible sur le devis)', (
              <textarea value={form.note_devis} onChange={e => set('note_devis', e.target.value)}
                rows={2} placeholder=""
                style={{ ...inputStyle, resize: 'vertical' }} />
            ))}
            {champ('Note interne (non imprimée)', (
              <textarea value={form.notes_internes} onChange={e => set('notes_internes', e.target.value)}
                rows={2} placeholder="Notes visibles uniquement dans l'appli"
                style={{ ...inputStyle, resize: 'vertical' }} />
            ))}
          </div>

        </div>
      </div>

      {/* Modal prévisualisation */}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1.5rem', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ color: 'var(--text-primary)' }}>Prévisualisation du devis</h3>
            <button className="btn-secondary" onClick={() => setPreview(false)}>✕ Fermer</button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', background: '#f0f0f0', padding: '2rem', display: 'flex', justifyContent: 'center' }}>
            <div style={{ background: '#fff', width: '210mm', minHeight: '297mm', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
              dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
      )}
    </div>
  );
}
